/** Enterprise inbound reply resolution (IMAP → lead → campagne stop). */
import { isOutOfOfficeOrAutoreply, } from './inboundAutoreply.js';
export { isOutOfOfficeOrAutoreply, subjectLooksLikeAutoreply } from './inboundAutoreply.js';
/** Normalize RFC Message-Ids for reliable DB lookup. */
export function normalizeInternetMessageId(raw) {
    if (raw == null)
        return null;
    let s = String(raw).trim();
    if (!s)
        return null;
    if (!s.startsWith('<'))
        s = `<${s.replace(/^<|>$/g, '')}>`;
    if (!s.endsWith('>'))
        s = `${s}>`;
    return s.toLowerCase();
}
/** All variants to match against stored outbound ids (providers differ on brackets/case). */
export function messageIdLookupCandidates(raw) {
    if (raw == null)
        return [];
    const trimmed = String(raw).trim();
    if (!trimmed)
        return [];
    const normalized = normalizeInternetMessageId(trimmed);
    const inner = normalized?.slice(1, -1) ?? trimmed.replace(/^<|>$/g, '');
    const set = new Set();
    for (const v of [trimmed, trimmed.toLowerCase(), normalized, inner, `<${inner}>`, `<${inner}>`.toLowerCase()]) {
        if (v)
            set.add(v.toLowerCase());
    }
    return [...set];
}
export function parseReferencesHeader(refs) {
    if (refs == null)
        return [];
    const parts = [];
    if (Array.isArray(refs)) {
        for (const r of refs)
            parts.push(...String(r).split(/\s+/));
    }
    else {
        parts.push(...String(refs).split(/\s+/));
    }
    const out = [];
    for (const p of parts) {
        const c = messageIdLookupCandidates(p);
        if (c[0])
            out.push(c[0]);
    }
    return [...new Set(out)];
}
export function extractEmailAddress(from) {
    if (!from)
        return null;
    const s = from.trim();
    const angle = s.match(/<([^>]+)>/);
    if (angle?.[1])
        return angle[1].trim().toLowerCase();
    if (s.includes('@'))
        return s.toLowerCase();
    return null;
}
const AUTO_SENDER = /(?:mailer-daemon|postmaster|noreply|no-reply|donotreply|bounce|mail-daemon)/i;
export function isAutomatedInboundSender(from) {
    if (!from)
        return false;
    if (AUTO_SENDER.test(from))
        return true;
    const email = extractEmailAddress(from);
    if (!email)
        return false;
    return AUTO_SENDER.test(email);
}
export function isLikelyHumanReply(ctx) {
    if (ctx.isBounce)
        return false;
    if (isAutomatedInboundSender(ctx.from))
        return false;
    return Boolean(extractEmailAddress(ctx.from));
}
async function findOutboundByMessageIds(querier, projectId, ids) {
    if (!ids.length)
        return null;
    const candidates = [...new Set(ids.flatMap((id) => messageIdLookupCandidates(id)))];
    if (!candidates.length)
        return null;
    const { rows } = await querier.query(`SELECT id, lead_id, campaign_id
       FROM mail_messages
       WHERE project_id = $1
         AND direction = 'outbound'
         AND status = 'sent'
         AND lower(trim(internet_message_id)) = ANY($2::text[])
       ORDER BY (lead_id IS NOT NULL) DESC, sent_at DESC NULLS LAST
       LIMIT 1`, [projectId, candidates]);
    if (rows[0])
        return rows[0];
    const loose = [...new Set(candidates.map((c) => c.replace(/^<|>$/g, '')).filter((c) => c.length > 12))];
    if (!loose.length)
        return null;
    const { rows: fuzzy } = await querier.query(`SELECT id, lead_id, campaign_id
       FROM mail_messages
       WHERE project_id = $1
         AND direction = 'outbound'
         AND status = 'sent'
         AND lower(internet_message_id) LIKE ANY(
           SELECT '%' || lower(x) || '%' FROM unnest($2::text[]) AS x
         )
       ORDER BY (lead_id IS NOT NULL) DESC, sent_at DESC NULLS LAST
       LIMIT 1`, [projectId, loose]);
    return fuzzy[0] ?? null;
}
function normalizeSubjectForThread(subject) {
    return subject.replace(/^(re|fw|fwd):\s+/i, '').trim().toLowerCase();
}
async function findOutboundBySubjectAndSender(querier, projectId, subject, senderEmail) {
    const norm = normalizeSubjectForThread(subject);
    if (!norm)
        return null;
    const { rows } = await querier.query(`SELECT om.id, om.lead_id, om.campaign_id
       FROM mail_messages om
       INNER JOIN leads l ON l.id = om.lead_id
       WHERE om.project_id = $1
         AND om.direction = 'outbound'
         AND om.status = 'sent'
         AND l.email_normalized = $2
         AND lower(trim(regexp_replace(coalesce(om.subject, ''), '^(re|fw|fwd):\\s+', '', 'i'))) = $3
       ORDER BY om.sent_at DESC NULLS LAST
       LIMIT 1`, [projectId, senderEmail.toLowerCase(), norm]);
    return rows[0] ?? null;
}
async function findLeadByRecentOutboundToSender(querier, projectId, senderEmail, daysBack = 90) {
    const { rows } = await querier.query(`SELECT l.id
       FROM leads l
       WHERE l.project_id = $1
         AND l.email_normalized = $2
         AND l.merged_into_lead_id IS NULL
         AND EXISTS (
           SELECT 1 FROM mail_messages om
           WHERE om.lead_id = l.id
             AND om.project_id = $1
             AND om.direction = 'outbound'
             AND om.status = 'sent'
             AND om.sent_at > now() - ($3::int || ' days')::interval
         )
       ORDER BY (
         SELECT max(om2.sent_at) FROM mail_messages om2
         WHERE om2.lead_id = l.id AND om2.direction = 'outbound' AND om2.status = 'sent'
       ) DESC NULLS LAST
       LIMIT 1`, [projectId, senderEmail.toLowerCase(), daysBack]);
    return rows[0]?.id ?? null;
}
/**
 * Resolve which lead an inbound message belongs to (thread headers first, then sender heuristic).
 */
export async function resolveInboundLeadMatch(querier, ctx) {
    if (!isLikelyHumanReply(ctx))
        return null;
    const threadIds = [
        ...messageIdLookupCandidates(ctx.inReplyTo),
        ...ctx.references.flatMap((r) => messageIdLookupCandidates(r)),
    ];
    if (threadIds.length) {
        const outbound = await findOutboundByMessageIds(querier, ctx.projectId, threadIds);
        if (outbound?.lead_id) {
            return {
                leadId: outbound.lead_id,
                outboundMailId: outbound.id,
                campaignId: outbound.campaign_id,
                method: ctx.inReplyTo ? 'in_reply_to' : 'references',
            };
        }
    }
    const sender = extractEmailAddress(ctx.from);
    if (!sender)
        return null;
    const subject = (ctx.subject ?? '').trim();
    if (subject) {
        const bySubject = await findOutboundBySubjectAndSender(querier, ctx.projectId, subject, sender);
        if (bySubject?.lead_id) {
            return {
                leadId: bySubject.lead_id,
                outboundMailId: bySubject.id,
                campaignId: bySubject.campaign_id,
                method: 'subject_thread',
            };
        }
    }
    const leadId = await findLeadByRecentOutboundToSender(querier, ctx.projectId, sender);
    if (leadId) {
        const looksLikeReply = /^re:\s/i.test(subject) || /^fw:\s/i.test(subject);
        return {
            leadId,
            outboundMailId: null,
            campaignId: null,
            method: looksLikeReply ? 'subject_thread' : 'sender_recent_outbound',
        };
    }
    return null;
}
export async function leadInboundSideEffectEventExists(querier, leadId, inboundInternetMessageId) {
    if (!inboundInternetMessageId)
        return false;
    const { rows } = await querier.query(`SELECT EXISTS (
       SELECT 1 FROM lead_events
       WHERE lead_id = $1
         AND event_type IN ('mail_reply', 'mail_bounce', 'mail_auto_reply')
         AND payload->>'messageId' = $2
     ) AS ok`, [leadId, inboundInternetMessageId]);
    return Boolean(rows[0]?.ok);
}
/** @deprecated use leadInboundSideEffectEventExists */
export const leadReplyEventExists = leadInboundSideEffectEventExists;
/** Stop enrollments, update lead, emit timeline (idempotent per inbound Message-Id). */
export async function applyInboundReplySideEffects(querier, params) {
    const { leadId, inboundInternetMessageId, isBounce } = params;
    if (await leadInboundSideEffectEventExists(querier, leadId, inboundInternetMessageId)) {
        return 'duplicate';
    }
    const autoreply = !isBounce &&
        isOutOfOfficeOrAutoreply({
            subject: params.subject,
            autoSubmitted: params.autoreply?.autoSubmitted,
            xAutoreply: params.autoreply?.xAutoreply,
            precedence: params.autoreply?.precedence,
        });
    if (autoreply) {
        await querier.query(`INSERT INTO lead_events (lead_id, event_type, payload) VALUES ($1, 'mail_auto_reply', $2::jsonb)`, [
            leadId,
            JSON.stringify({
                subject: params.subject,
                from: params.from,
                messageId: inboundInternetMessageId,
                matchMethod: params.matchMethod,
                outboundMailId: params.outboundMailId,
            }),
        ]);
        return 'auto_reply';
    }
    const eventType = isBounce ? 'mail_bounce' : 'mail_reply';
    await querier.query(`INSERT INTO lead_events (lead_id, event_type, payload) VALUES ($1, $2, $3::jsonb)`, [
        leadId,
        eventType,
        JSON.stringify({
            subject: params.subject,
            from: params.from,
            messageId: inboundInternetMessageId,
            matchMethod: params.matchMethod,
            outboundMailId: params.outboundMailId,
        }),
    ]);
    if (!isBounce) {
        await querier.query(`UPDATE leads SET status = 'replied', updated_at = now() WHERE id = $1`, [leadId]);
        await querier.query(`UPDATE campaign_enrollments SET status = 'stopped_reply', updated_at = now()
         WHERE lead_id = $1 AND status IN ('active', 'paused')`, [leadId]);
    }
    else {
        await querier.query(`UPDATE campaign_enrollments SET status = 'stopped_bounce', updated_at = now()
         WHERE lead_id = $1 AND status IN ('active', 'paused')`, [leadId]);
    }
    return 'applied';
}
//# sourceMappingURL=inboundReply.js.map